import { defaultServiceLanguage, ServiceLanguage } from "@albertine/i18n";
import {
    BillingPeriodId,
    CommitmentPeriodId,
    defaultBillingPeriodId,
    defaultCommitmentPeriodId,
    defaultCommitmentPeriods,
    defaultMemberSource,
    defaultTrialPeriodId,
    Email,
    isNoTrial,
    MemberSourceId,
    type Membership,
    MembershipTierId,
    PhoneNumber,
    TrialId,
} from "@albertine/types";
import type { CreateMemberRequest } from "@albertine/types/openapi";
import {
    type MembershipTier,
    type Tenant,
    TenantSchema,
} from "@albertine/types/openapi/lmt";
import { addMonths, format } from "date-fns";
import { z } from "zod";
import {
    areTrialsEnabled,
    getTenantServiceLanguages,
} from "../../common/utils/tenant.util";
import { toUpdateMemberMembershipRequest } from "../../common/utils/updateMemberMembershipRequest.util";

const isoDateFormat = "yyyy-MM-dd";

export const emailInUseMessage = "A member with this email address already exists";
export const phoneNumberInUseMessage = "A member with this phone number already exists";

/** Contact details that the API reported as already belonging to a member. */
export type ContactDetailsInUse = {
    emails: string[];
    phoneNumbers: string[];
};

export const noContactDetailsInUse: ContactDetailsInUse = {
    emails: [],
    phoneNumbers: [],
};

// Email addresses are stored in lowercase, so the casing typed in doesn't matter.
export const NewMemberEmail = z
    .string()
    .trim()
    .min(1, { error: "Email address is needed" })
    .toLowerCase()
    .pipe(Email);

export const NewMemberPhoneNumber = z
    .string()
    .min(1, { error: "Phone number is needed" })
    .pipe(PhoneNumber);

/**
 * The form's fields in the order they are shown, so that the first issue is also the
 * first field to fix.
 */
export function newMemberFormSchema(inUse: ContactDetailsInUse) {
    return z.object({
        title: z.string().trim(),
        firstName: z.string().trim().min(1, { error: "First name is needed" }),
        lastName: z.string().trim().min(1, { error: "Last name is needed" }),
        phoneNumber: NewMemberPhoneNumber.refine(
            (phoneNumber) => !inUse.phoneNumbers.includes(phoneNumber),
            { error: phoneNumberInUseMessage },
        ),
        email: NewMemberEmail.refine((email) => !inUse.emails.includes(email), {
            error: emailInUseMessage,
        }),
        source: MemberSourceId,
        additionalNoteOnSource: z.string().trim(),
        assignedAgentId: z.string().min(1, { error: "An assignee is needed" }),
        tenant: TenantSchema,
        tier: z
            .string()
            .min(1, { error: "A membership tier is needed" })
            .pipe(MembershipTierId),
        trial: TrialId,
        serviceLanguage: ServiceLanguage,
        commitmentPeriod: CommitmentPeriodId,
        billingPeriod: BillingPeriodId,
        startDate: z.date({ error: "Membership start date is not valid" }),
    });
}

type NewMemberFormSchema = ReturnType<typeof newMemberFormSchema>;
export type NewMemberFormInput = z.input<NewMemberFormSchema>;
export type NewMemberFormOutput = z.output<NewMemberFormSchema>;

type MembershipChoices = Pick<NewMemberFormInput, "tier" | "trial" | "serviceLanguage">;

export function newMemberFormDefaults(
    tenant: Tenant,
    membershipTiers: MembershipTier[],
    assignedAgentId: string | undefined,
): NewMemberFormInput {
    return {
        title: "",
        firstName: "",
        lastName: "",
        phoneNumber: "",
        email: "",
        source: defaultMemberSource,
        additionalNoteOnSource: "",
        assignedAgentId: assignedAgentId ?? "",
        tenant,
        ...membershipChoicesForTenant(
            {
                tenant,
                tier: "",
                trial: defaultTrialPeriodId,
                serviceLanguage: defaultServiceLanguage,
            },
            tenant,
            membershipTiers,
        ),
        commitmentPeriod: defaultCommitmentPeriodId,
        billingPeriod: defaultBillingPeriodId,
        startDate: new Date(),
    };
}

export function membershipOptionsForTenant(
    tenant: Tenant,
    membershipTiers: MembershipTier[],
) {
    return {
        tiers: membershipTiers.filter((tier) => tier.tenant === tenant),
        languages: getTenantServiceLanguages(tenant),
        trialsEnabled: areTrialsEnabled(tenant),
    };
}

/**
 * Keeps the current choices that the tenant offers and replaces the others with the
 * tenant's defaults, so switching tenants never leaves a stale tier or language.
 */
export function membershipChoicesForTenant(
    current: MembershipChoices & { tenant: Tenant },
    tenant: Tenant,
    membershipTiers: MembershipTier[],
): MembershipChoices {
    const { tiers, languages, trialsEnabled } = membershipOptionsForTenant(
        tenant,
        membershipTiers,
    );
    const defaultTier = tiers.find((tier) => tier.default) ?? tiers[0];

    return {
        tier: tiers.some((tier) => tier.id === current.tier)
            ? current.tier
            : (defaultTier?.id ?? ""),
        // Tenants without trials only get paid memberships, which isn't a choice made
        // for the trial, so it shouldn't carry over to a tenant with trials.
        trial: !trialsEnabled
            ? "no-trial"
            : areTrialsEnabled(current.tenant)
              ? current.trial
              : defaultTrialPeriodId,
        serviceLanguage: languages.includes(current.serviceLanguage)
            ? current.serviceLanguage
            : (languages[0] ?? defaultServiceLanguage),
    };
}

export function paidMembershipEndDate(
    startDate: Date,
    commitmentPeriod: CommitmentPeriodId,
): Date {
    const months = defaultCommitmentPeriods.find(
        (period) => period.id === commitmentPeriod,
    )?.value;
    if (months === undefined) {
        throw new Error(`no length for commitment period ${commitmentPeriod}`);
    }
    return addMonths(startDate, months);
}

export function toCreateMemberRequest(
    values: NewMemberFormOutput,
): CreateMemberRequest {
    return {
        title: values.title,
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: values.phoneNumber,
        email: values.email,
        membership: toUpdateMemberMembershipRequest(toMembership(values)),
        source: values.source,
        serviceLanguage: values.serviceLanguage,
        additionalNoteOnSource: values.additionalNoteOnSource || undefined,
        assignedAgentId: values.assignedAgentId,
    };
}

function toMembership(values: NewMemberFormOutput): Membership {
    if (isNoTrial(values.trial)) {
        const endDate = paidMembershipEndDate(
            values.startDate,
            values.commitmentPeriod,
        );

        return {
            tier: values.tier,
            trial: values.trial,
            commitmentPeriod: values.commitmentPeriod,
            billingPeriod: values.billingPeriod,
            startDate: format(values.startDate, isoDateFormat),
            endDate: format(endDate, isoDateFormat),
            cancellation: null,
        };
    }

    return {
        tier: values.tier,
        trial: values.trial,
        startTrialDate: undefined,
        endTrialDate: undefined,
        cancellation: null,
        extendedFor: null,
    };
}
